import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';

import { mensajeDe } from '@/api/errors';
import { ContenidoProtegido, ImagenProtegida } from '@/components/ContenidoProtegido';
import { crossFade, moveIndicator, useSwipeCards } from '@/lib/motion';
import { cn } from '@/lib/utils';

import { iconoEspecialidad } from './especialidades';

/** Navegación del módulo Curso ENARM (skill §4): CLASES · BIBLIOTECA · EXÁMENES · AVANCES · TEMARIO. */
const MODULO = [
  { etiqueta: 'Clases', ruta: '/curso-enarm/clases' },
  { etiqueta: 'Talleres', ruta: '/curso-enarm/talleres' },
  { etiqueta: 'Biblioteca', ruta: '/curso-enarm/biblioteca' },
  { etiqueta: 'Exámenes', ruta: '/curso-enarm/examenes-prime' },
  { etiqueta: 'Avances', ruta: '/curso-enarm/avances' },
  { etiqueta: 'Temario', ruta: '/curso-enarm/temario' },
];

export function TabsModulo() {
  const { pathname } = useLocation();
  const indicador = useRef<HTMLSpanElement>(null);
  const contenedor = useRef<HTMLElement>(null);
  const activa = MODULO.findIndex((m) => pathname.startsWith(m.ruta));
  // El "fondo" activo se desliza entre pestañas (moveIndicator anima x y width de un elemento).
  useLayoutEffect(() => {
    const tab = contenedor.current?.querySelectorAll<HTMLElement>('a')[activa] ?? null;
    if (indicador.current) indicador.current.style.opacity = tab ? '1' : '0';
    moveIndicator(indicador.current, tab);
  }, [activa]);
  return (
    <nav
      ref={contenedor}
      aria-label="Curso ENARM"
      className="relative -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0"
    >
      <span
        ref={indicador}
        aria-hidden
        className="pointer-events-none absolute top-0 left-0 h-[calc(100%-0.25rem)] rounded-[10px] bg-azul-600 opacity-0"
      />
      {MODULO.map((m, i) => (
        <NavLink
          key={m.ruta}
          to={m.ruta}
          className={cn('btn-modulo relative shrink-0', i === activa && 'tab-activa')}
        >
          {m.etiqueta}
        </NavLink>
      ))}
    </nav>
  );
}

export function TileEspecialidad({
  nombre,
  activo,
  conteo,
  onClick,
}: {
  nombre: string;
  activo: boolean;
  conteo?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-tile
      className="tile-especialidad min-h-32 text-left"
      data-activo={activo}
      aria-pressed={activo}
      onClick={onClick}
    >
      <span className="tile-icono">
        <img src={iconoEspecialidad(nombre)} alt="" className="h-14 w-14 object-contain" />
      </span>
      <span className="tile-nombre">
        {nombre}
        {conteo !== undefined && <span className="ml-1 opacity-70">({conteo})</span>}
      </span>
    </button>
  );
}

/** Filtro por especialidad (botones de contorno verde con conteo; activo en azul). */
export function FiltroEspecialidad({
  opciones,
  valor,
  onChange,
}: {
  opciones: { nombre: string; conteo: number }[];
  valor: string | null;
  onChange: (v: string | null) => void;
}) {
  const total = opciones.reduce((s, o) => s + o.conteo, 0);
  return (
    <div role="group" aria-label="Especialidad" className="flex flex-wrap justify-center gap-2">
      {[{ nombre: null as string | null, conteo: total }, ...opciones].map((o) => (
        <button
          key={o.nombre ?? '__todas'}
          type="button"
          className="btn-modulo"
          aria-pressed={valor === o.nombre}
          data-state={valor === o.nombre ? 'active' : undefined}
          onClick={() => {
            onChange(o.nombre);
          }}
        >
          {o.nombre ?? 'Todas'}: {o.conteo}
        </button>
      ))}
    </div>
  );
}

export function Cargando({ alto = 'h-64' }: { alto?: string }) {
  return <div className={cn('card-vidrio animate-pulse', alto)} aria-busy aria-label="Cargando" />;
}

export function ErrorCarga({ error, children }: { error: unknown; children?: ReactNode }) {
  return (
    <section className="panel-vidrio mx-auto max-w-xl text-center">
      <p className="font-bold text-rojo-texto">{mensajeDe(error)}</p>
      {children}
    </section>
  );
}

/**
 * Visor de láminas protegido (presentaciones, flashcards, ultra resúmenes). Teclado ← →,
 * deslizar en móvil, pantalla completa y tira de miniaturas numeradas. `onCambio` recibe el
 * número de lámina (1…n) para guardar el avance.
 */
export function VisorLaminas({
  laminas,
  inicial = 1,
  contenido,
  onCambio,
  onExpirada,
  titulo,
  actual: controlada,
  miniaturas = true,
}: {
  laminas: { clave: string | number; imageUrl: string; nombre?: string }[];
  inicial?: number;
  /** Modo controlado: el padre decide la lámina (1…n) y la cambia con `onCambio`. */
  actual?: number;
  /** Tira de números para saltar de lámina (los manuales ya tienen su índice). */
  miniaturas?: boolean;
  contenido: [string, string | number];
  onCambio?: (n: number) => void;
  onExpirada?: () => unknown;
  titulo: string;
}) {
  const [interna, setActual] = useState(() => Math.min(Math.max(inicial, 1), laminas.length || 1));
  const actual = controlada ?? interna;
  const [completa, setCompleta] = useState(false);
  const marco = useRef<HTMLDivElement>(null);
  const lamina = useRef<HTMLDivElement>(null);
  const total = laminas.length;

  const ir = useCallback(
    (n: number) => {
      const destino = Math.min(Math.max(n, 1), total);
      if (destino === actual) return;
      crossFade(null, lamina.current);
      setActual(destino);
      onCambio?.(destino);
    },
    [actual, total, onCambio],
  );
  const siguiente = useCallback(() => {
    ir(actual + 1);
  }, [ir, actual]);
  const anterior = useCallback(() => {
    ir(actual - 1);
  }, [ir, actual]);
  useSwipeCards(lamina, { onNext: siguiente, onPrev: anterior });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowRight') siguiente();
      if (e.key === 'ArrowLeft') anterior();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [siguiente, anterior]);

  useEffect(() => {
    const onFs = () => {
      setCompleta(document.fullscreenElement === marco.current);
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
    };
  }, []);

  if (total === 0) {
    return <p className="text-center text-texto-2">Este contenido aún no tiene láminas.</p>;
  }
  const l = laminas[actual - 1];

  return (
    <div
      ref={marco}
      className={cn('space-y-3', completa && 'grid place-items-center bg-noche-950 p-4')}
    >
      <ContenidoProtegido
        contenido={contenido}
        className="rounded-[var(--radius-card)] bg-black/30"
      >
        <div ref={lamina} className="touch-pan-y">
          {l && (
            <ImagenProtegida
              src={l.imageUrl}
              clave={l.clave}
              alt={`${titulo}: lámina ${String(actual)} de ${String(total)}`}
              className={cn(
                'mx-auto w-auto max-w-full',
                completa ? 'max-h-[85vh]' : 'max-h-[75vh]',
              )}
              {...(onExpirada ? { onExpirada } : {})}
            />
          )}
        </div>
      </ContenidoProtegido>
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className="btn-cs btn-vidrio"
          onClick={anterior}
          disabled={actual === 1}
          aria-label="Lámina anterior"
        >
          <ChevronLeft size={18} /> <span className="hidden sm:inline">Anterior</span>
        </button>
        <p className="text-sm font-bold text-texto-2 tabular" aria-live="polite">
          {actual} / {total}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-cs btn-vidrio"
            aria-label={completa ? 'Salir de pantalla completa' : 'Pantalla completa'}
            onClick={() => {
              if (completa) void document.exitFullscreen();
              else void marco.current?.requestFullscreen();
            }}
          >
            {completa ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
          <button
            type="button"
            className="btn-cs btn-primario"
            onClick={siguiente}
            disabled={actual === total}
            aria-label="Lámina siguiente"
          >
            <span className="hidden sm:inline">Siguiente</span> <ChevronRight size={18} />
          </button>
        </div>
      </div>
      {miniaturas && !completa && total > 1 && (
        <nav aria-label="Ir a la lámina" className="flex flex-wrap justify-center gap-1.5">
          {laminas.map((x, i) => (
            <button
              key={x.clave}
              type="button"
              onClick={() => {
                ir(i + 1);
              }}
              aria-current={i + 1 === actual ? 'true' : undefined}
              title={x.nombre}
              className={cn(
                'h-8 min-w-8 rounded-lg px-2 text-xs font-extrabold tabular transition-colors',
                i + 1 === actual
                  ? 'bg-azul-600 text-white'
                  : 'border border-verde-500/60 text-texto-2 hover:bg-white/10',
              )}
            >
              {i + 1}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

/** Reproductor (Vimeo/YouTube) en tarjeta 16:9; el video lo protege su proveedor. */
export function Reproductor({ embedUrl, titulo }: { embedUrl: string; titulo: string }) {
  return (
    <div className="card-vidrio overflow-hidden p-0">
      <div className="relative aspect-video">
        <iframe
          src={embedUrl}
          title={titulo}
          className="absolute inset-0 h-full w-full"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    </div>
  );
}

/**
 * Texto con el marcado del legacy (`**negritas**`, `__cursivas__`, saltos de línea) sin HTML
 * crudo: se arma con nodos de React.
 */
export function TextoMarcado({ texto }: { texto: string }) {
  return (
    <>
      {texto.split('\n').map((linea, i) => (
        <span key={i} className="block">
          {linea
            .split(/(\*\*[^*]+\*\*|__[^_]+__)/g)
            .map((p, j) =>
              p.startsWith('**') && p.endsWith('**') ? (
                <strong key={j}>{p.slice(2, -2)}</strong>
              ) : p.startsWith('__') && p.endsWith('__') ? (
                <em key={j}>{p.slice(2, -2)}</em>
              ) : (
                p
              ),
            )}
        </span>
      ))}
    </>
  );
}
